"use client"

import { useState, useEffect } from "react"
import { useLocale } from "next-intl"
import { useRouter, usePathname } from "@/i18n/navigation"
import { Button } from "@/components/ui/button"
import { X } from "lucide-react"
import { useTranslations } from "next-intl"

const languages = [
  { value: "en-EU", label: "English (EU)" },
  { value: "de-AT", label: "Deutsch (Österreich)" },
]

// Map browser language codes to our locales
const browserLocaleMap: Record<string, string> = {
  'en': 'en-EU',
  'en-EU': 'en-EU',
  'en-GB': 'en-EU',
  'en-US': 'en-EU',
  'de': 'de-AT',
  'de-AT': 'de-AT',
  'de-DE': 'de-AT',
}

export function LocaleSuggestionBanner() {
  const [isVisible, setIsVisible] = useState(false)
  const [suggestedLocale, setSuggestedLocale] = useState<string | null>(null)
  const locale = useLocale()
  const router = useRouter()
  const pathname = usePathname()
  const t = useTranslations("language")

  useEffect(() => {
    // Only run on client side
    if (typeof window === 'undefined') return

    // Check if user has dismissed the banner
    const dismissed = localStorage.getItem("localeSuggestionDismissed")
    if (dismissed) return

    // Check if locale was explicitly set (via URL or cookie)
    const explicitLocale = sessionStorage.getItem("localeSet")
    if (explicitLocale) return

    // Check if we've already shown a suggestion in this session
    const suggestionShown = sessionStorage.getItem("localeSuggestionShown")
    if (suggestionShown) return

    // Detect browser's preferred language
    const browserLang = navigator.language || (navigator as { userLanguage?: string }).userLanguage
    if (!browserLang) return
    
    const detectedLocale = browserLocaleMap[browserLang] || browserLocaleMap[browserLang.split('-')[0]]

    // Only show if detected locale is different from current locale
    if (detectedLocale && detectedLocale !== locale) {
      // Use setTimeout to avoid synchronous setState in effect
      setTimeout(() => {
        setSuggestedLocale(detectedLocale)
        setIsVisible(true)
        // Mark that we've shown a suggestion
        sessionStorage.setItem("localeSuggestionShown", "true")
      }, 0)
    }
  }, [locale])

  const handleSwitch = () => {
    if (suggestedLocale) {
      sessionStorage.setItem("localeSet", "true")
      router.replace(pathname, { locale: suggestedLocale })
      setIsVisible(false)
    }
  }

  const handleDismiss = () => {
    localStorage.setItem("localeSuggestionDismissed", "true")
    setIsVisible(false)
  }

  if (!isVisible || !suggestedLocale) return null

  const suggestedLanguage = languages.find(lang => lang.value === suggestedLocale)

  return (
    <div className="bg-gray-900 text-white border-b border-gray-800 animate-in slide-in-from-top duration-300">
      <div className="container px-4 md:px-6 py-3">
        <div className="flex items-center justify-between gap-4">
          <p className="text-sm flex-1 text-center md:text-left">
            {t("suggestion.text", { 
              language: suggestedLanguage?.label || suggestedLocale 
            })}
          </p>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              size="sm"
              className="whitespace-nowrap"
              onClick={handleSwitch}
            >
              {t("suggestion.switch")}
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="text-white hover:bg-gray-800 h-8 w-8"
              onClick={handleDismiss}
              aria-label={t("suggestion.dismiss")}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
